import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { fetchPricesAndBestPractices } from '../../../services/openaiService';

const AIPriceFetchModal = ({ isOpen, onClose, dataType, onApplyPrices }) => {
  const [loading, setLoading] = useState(false);
  const [itemQuery, setItemQuery] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleFetchPrices = async () => {
    if (!itemQuery?.trim()) return;

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await fetchPricesAndBestPractices(itemQuery, dataType);
      setResults(response?.data);
    } catch (err) {
      setError(err?.message || 'Failed to fetch prices');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (results?.suggestedItems) {
      onApplyPrices(results?.suggestedItems);
      onClose();
      setItemQuery('');
      setResults(null);
    }
  };

  const handleClose = () => {
    onClose();
    setItemQuery('');
    setResults(null);
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-card border border-border rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="Sparkles" size={24} className="text-primary" />
              AI Price Fetcher
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Get current market prices and best practices from web
            </p>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              What are you looking for?
            </label>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder={`e.g., "Fiber optic cable 24 core" or "Senior Network Engineer rates"`}
                value={itemQuery}
                onChange={(e) => setItemQuery(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && handleFetchPrices()}
                className="flex-1"
              />
              <Button
                variant="default"
                iconName="Search"
                onClick={handleFetchPrices}
                loading={loading}
                disabled={!itemQuery?.trim() || loading}
              >
                Fetch
              </Button>
            </div>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-sm text-muted-foreground">Analyzing market data and best practices...</p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-destructive mb-1">Error</p>
                  <p className="text-xs text-destructive/80">{error}</p>
                </div>
              </div>
            </div>
          )}

          {results && (
            <div className="space-y-4">
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="Lightbulb" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground mb-2">Market Insights</p>
                    <p className="text-xs text-muted-foreground">{results?.marketInsights}</p>
                  </div>
                </div>
              </div>

              {results?.bestPractices && results?.bestPractices?.length > 0 && (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="CheckCircle" size={18} className="text-success" />
                    Best Practices
                  </p>
                  <ul className="space-y-2">
                    {results?.bestPractices?.map((practice, index) => (
                      <li key={index} className="text-xs text-muted-foreground flex items-start gap-2">
                        <span className="text-primary mt-0.5">•</span>
                        <span>{practice}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {results?.suggestedItems && results?.suggestedItems?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-foreground mb-3">Suggested Items</p>
                  <div className="space-y-2">
                    {results?.suggestedItems?.map((item, index) => (
                      <div key={index} className="p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-smooth">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">{item?.name || item?.itemName || item?.roleName || item?.softwareName || item?.vendorName}</p>
                            {item?.description && (
                              <p className="text-xs text-muted-foreground mt-1">{item?.description}</p>
                            )}
                            {item?.category && (
                              <span className="inline-block px-2 py-0.5 bg-muted text-xs text-muted-foreground rounded mt-2 capitalize">
                                {item?.category}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-primary">
                              ${item?.unitCost || item?.costPerDay || item?.cost || item?.monthlyCost || 0}
                            </p>
                            {item?.priceRange && (
                              <p className="text-xs text-muted-foreground mt-1">{item?.priceRange}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {results?.confidence && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="Info" size={14} />
                  <span>Confidence Level: <span className="font-medium capitalize">{results?.confidence}</span></span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleClose}
            fullWidth
            className="sm:flex-1"
          >
            Close
          </Button>
          {results?.suggestedItems && (
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={handleApply}
              fullWidth
              className="sm:flex-1"
            >
              Add to Master Data
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIPriceFetchModal;